import * as THREE from "three";
import { GPUComputationRenderer } from "three/examples/jsm/misc/GPUComputationRenderer.js";

import simulationPositionShader from "./shaders/simulationPositionShader.glsl";
import simulationVelocityShader from "./shaders/simulationVelocityShader.glsl";

const PARAMS = {
    perceptionRadius: 4.8,
    separationRadius: 1.8,
    obstacleRadius: 12.0,
    avoidanceSamples: 20.0,
    avoidanceWeight: 2.0,
    maxSpeed: 0.18,
    maxForce: 0.003,
    separationWeight: 0.9,
    alignmentWeight: 0.9,
    cohesionWeight: 0.25,
    originForceWeight: 0.0005,
    boundaryForceWeight: 0.01,
    wanderWeight: 0.55,
    wanderDistance: 2.5,
    wanderRadius: 2.0,
    wanderSpeed: 1.5,
    wiggleSpeed: 20.0,
    wiggleFrequency: 2.7,
    wiggleIntensity: 0.15,
};

export default class BoidSimulation {
    constructor(size, boidColor, bounds, model, texture) {
        this.width = size;
        this.height = size;
        this.count = size * size;
        this.bounds = bounds;
        this.boidColor = boidColor;
        this.model = model;
        this.texture = texture;
        this.experience = window.experience;
        this.renderer = this.experience.renderer.instance;
        this.debug = this.experience.debug;

        this._initGPUCompute();
        this._initMesh();
        // only for debug purposes
        // this._visualizeObstacles();

        if (this.debug.active) {
            this.setDebug();
        }
    }

    _getObstaclesPositions() {
        const vectorArray = [
            // Obstacle points for the central Stone Arc
            new THREE.Vector3(-15.5, 6.329 - 20.5, 19.67),
            new THREE.Vector3(-13.32, 18.724 - 20.5, 16.184),
            new THREE.Vector3(-7.466, 29.527 - 20.5, 9.539),
            new THREE.Vector3(0.9, 35 - 20.5, -0.49745),
            new THREE.Vector3(8.944, 31.893 - 20.5, -11.66),
            new THREE.Vector3(12.816, 21.399 - 20.5, -16.266),
            new THREE.Vector3(13.636, 6.2669 - 20.5, -16.84),
        ];
        return vectorArray;
    }

    // only for debug purposes
    _visualizeObstacles() {
        this.scene = this.experience.scene;

        const axesHelper = new THREE.AxesHelper(100);
        this.scene.add(axesHelper);

        const obstacles = this._getObstaclesPositions();
        const geometry = new THREE.SphereGeometry(
            PARAMS.obstacleRadius,
            16,
            16,
        );
        const material = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            wireframe: true,
        });

        obstacles.forEach((pos) => {
            const mesh = new THREE.Mesh(geometry, material);
            mesh.position.copy(pos);
            this.scene.add(mesh);
        });
    }

    _initGPUCompute() {
        this.gpuCompute = new GPUComputationRenderer(
            this.width,
            this.height,
            this.renderer,
        );
        // // Below is suboptimal as fish movements become more rigid and less smooth
        // // Use HalfFloatType if available to save 50% memory on compute textures
        // if (this.experience.renderer.instance.extensions.get('EXT_color_buffer_half_float')) {
        //     this.gpuCompute.setDataType(THREE.HalfFloatType);
        // }

        const dtPosition = this.gpuCompute.createTexture();
        const dtVelocity = this.gpuCompute.createTexture();
        this._fillInitialTextures(dtPosition, dtVelocity);

        this.positionVariable = this.gpuCompute.addVariable(
            "texturePosition",
            simulationPositionShader,
            dtPosition,
        );
        this.velocityVariable = this.gpuCompute.addVariable(
            "textureVelocity",
            simulationVelocityShader,
            dtVelocity,
        );

        this.gpuCompute.setVariableDependencies(this.positionVariable, [
            this.positionVariable,
            this.velocityVariable,
        ]);
        this.gpuCompute.setVariableDependencies(this.velocityVariable, [
            this.positionVariable,
            this.velocityVariable,
        ]);

        this.positionVariable.material.uniforms.delta = { value: 0 };

        const velUniforms = this.velocityVariable.material.uniforms;
        velUniforms.delta = { value: 0 };
        velUniforms.uTimeB = { value: 0 };
        velUniforms.uObstacles = { value: this._getObstaclesPositions() };
        velUniforms.uObstacleRadius = { value: PARAMS.obstacleRadius };
        velUniforms.uAvoidanceSamples = { value: PARAMS.avoidanceSamples };
        velUniforms.uAvoidanceWeight = { value: PARAMS.avoidanceWeight };
        velUniforms.perceptionRadius = { value: PARAMS.perceptionRadius };
        velUniforms.separationRadius = { value: PARAMS.separationRadius };
        velUniforms.maxSpeed = { value: PARAMS.maxSpeed };
        velUniforms.maxForce = { value: PARAMS.maxForce };
        velUniforms.separationWeight = { value: PARAMS.separationWeight };
        velUniforms.alignmentWeight = { value: PARAMS.alignmentWeight };
        velUniforms.cohesionWeight = { value: PARAMS.cohesionWeight };
        velUniforms.originForceWeight = { value: PARAMS.originForceWeight };
        velUniforms.boundaryForceWeight = { value: PARAMS.boundaryForceWeight };
        velUniforms.wanderWeight = { value: PARAMS.wanderWeight };
        velUniforms.wanderDistance = { value: PARAMS.wanderDistance };
        velUniforms.wanderRadius = { value: PARAMS.wanderRadius };
        velUniforms.wanderSpeed = { value: PARAMS.wanderSpeed };
        velUniforms.boundsMin = { value: this.bounds.min };
        velUniforms.boundsMax = { value: this.bounds.max };

        const error = this.gpuCompute.init();
        if (error !== null) {
            console.error(error);
        }
    }

    _fillInitialTextures(texturePosition, textureVelocity) {
        const posArray = texturePosition.image.data;
        const velArray = textureVelocity.image.data;

        const obstacles = this._getObstaclesPositions();
        const obsRadius = PARAMS.obstacleRadius;

        const isInsideObstacle = (pos) => {
            for (let i = 0; i < obstacles.length; i++) {
                if (pos.distanceTo(obstacles[i]) < obsRadius) {
                    return true;
                }
            }
            return false;
        };

        const numSchools = Math.floor(Math.random() * 11) + 8; // 8 to 18 schools
        const schools = [];
        for (let i = 0; i < numSchools; i++) {
            let center = new THREE.Vector3();
            let valid = false;
            let attempts = 0;
            while (!valid && attempts < 100) {
                center.set(
                    (Math.random() - 0.5) * this.bounds.width,
                    (Math.random() - 0.5) * this.bounds.height,
                    (Math.random() - 0.5) * this.bounds.depth,
                );
                if (!isInsideObstacle(center)) {
                    valid = true;
                }
                attempts++;
            }

            const direction = new THREE.Vector3(
                Math.random() - 0.5,
                (Math.random() - 0.5) * 0.1,
                Math.random() - 0.5,
            ).normalize();

            schools.push({ center, direction, boids: [] });
        }

        const separationRadiusSq =
            PARAMS.separationRadius * PARAMS.separationRadius;

        for (let i = 0; i < this.count; i++) {
            const schoolIndex = i % numSchools;
            const school = schools[schoolIndex];
            const pos = new THREE.Vector3();
            let placed = false;
            let attempts = 0;

            while (!placed && attempts < 100) {
                // Random point in a sphere around school center
                const spawnRadius = 8.0;
                const phi = Math.random() * Math.PI * 2;
                const theta = Math.acos(2 * Math.random() - 1);
                const r = spawnRadius * Math.pow(Math.random(), 1 / 3);

                pos.set(
                    school.center.x + r * Math.sin(theta) * Math.cos(phi),
                    school.center.y + r * Math.sin(theta) * Math.sin(phi),
                    school.center.z + r * Math.cos(theta),
                );

                // Boundary check
                pos.x = Math.max(
                    -this.bounds.width / 2,
                    Math.min(this.bounds.width / 2, pos.x),
                );
                pos.y = Math.max(
                    -this.bounds.height / 2,
                    Math.min(this.bounds.height / 2, pos.y),
                );
                pos.z = Math.max(
                    -this.bounds.depth / 2,
                    Math.min(this.bounds.depth / 2, pos.z),
                );

                if (!isInsideObstacle(pos)) {
                    let tooClose = false;
                    for (let j = 0; j < school.boids.length; j++) {
                        if (
                            pos.distanceToSquared(school.boids[j]) <
                            separationRadiusSq
                        ) {
                            tooClose = true;
                            break;
                        }
                    }
                    if (!tooClose) {
                        placed = true;
                    }
                }
                attempts++;
            }

            school.boids.push(pos.clone());

            const i4 = i * 4;
            posArray[i4 + 0] = pos.x;
            posArray[i4 + 1] = pos.y;
            posArray[i4 + 2] = pos.z;
            posArray[i4 + 3] = 1;

            const speed = (0.5 + Math.random() * 0.5) * PARAMS.maxSpeed;
            const velocity = school.direction.clone();
            velocity.x += (Math.random() - 0.5) * 0.2;
            velocity.y += (Math.random() - 0.5) * 0.2;
            velocity.z += (Math.random() - 0.5) * 0.2;
            velocity.normalize().multiplyScalar(speed);

            velArray[i4 + 0] = velocity.x;
            velArray[i4 + 1] = velocity.y;
            velArray[i4 + 2] = velocity.z;
            velArray[i4 + 3] = 1;
        }
    }

    _initMesh() {
        let geometry;
        if (this.model) {
            this.model.scene.traverse((child) => {
                if (child instanceof THREE.Mesh && !geometry) {
                    geometry = child.geometry.clone();
                    geometry.scale(0.5, 0.5, 0.5);
                }
            });
        }

        if (!geometry) {
            geometry = new THREE.ConeGeometry(0.2, 0.8, 8);
            geometry.rotateX(Math.PI / 2);
        }

        const reference = new Float32Array(this.count * 2);
        for (let i = 0; i < this.count; i++) {
            reference[i * 2 + 0] = ((i % this.width) + 0.5) / this.width;
            reference[i * 2 + 1] =
                (Math.floor(i / this.width) + 0.5) / this.height;
        }
        geometry.setAttribute(
            "reference",
            new THREE.InstancedBufferAttribute(reference, 2),
        );

        const wiggleOffset = new Float32Array(this.count * 1);
        for (let i = 0; i < this.count; i++) {
            wiggleOffset[i] = Math.random() * Math.PI * 2;
        }
        geometry.setAttribute(
            "wiggleOffset",
            new THREE.InstancedBufferAttribute(wiggleOffset, 1),
        );

        const wiggleSpeed = new Float32Array(this.count * 1);
        for (let i = 0; i < this.count; i++) {
            wiggleSpeed[i] = (Math.random() * 0.5 + 0.5) * PARAMS.wiggleSpeed; // Random speed factor between 0.5 and 1.0
        }
        geometry.setAttribute(
            "wiggleSpeed",
            new THREE.InstancedBufferAttribute(wiggleSpeed, 1),
        );

        // (optional) Gradient map for toon shading
        const gradientValues = new Uint8Array([
            0, 36, 72, 108, 144, 180, 216, 255,
        ]);
        const gradientTexture = new THREE.DataTexture(
            gradientValues,
            gradientValues.length,
            1,
            THREE.RedFormat,
        );
        gradientTexture.magFilter = THREE.NearestFilter;
        gradientTexture.minFilter = THREE.NearestFilter;
        gradientTexture.generateMipmaps = false;
        gradientTexture.needsUpdate = true;

        this.material = new THREE.MeshPhongMaterial({
            color: new THREE.Color(this.boidColor),
            map: this.texture,
            // gradientMap: gradientTexture,
        });

        this.material.userData.uniforms = {
            texturePosition: { value: null },
            textureVelocity: { value: null },
            uTimeB: { value: 0 },
            uWiggleFrequency: { value: PARAMS.wiggleFrequency },
            uWiggleIntensity: { value: PARAMS.wiggleIntensity },
        };

        this.material.onBeforeCompile = (shader) => {
            shader.uniforms.uTimeB = this.material.userData.uniforms.uTimeB;
            shader.uniforms.texturePosition =
                this.material.userData.uniforms.texturePosition;
            shader.uniforms.textureVelocity =
                this.material.userData.uniforms.textureVelocity;
            shader.uniforms.uWiggleFrequency =
                this.material.userData.uniforms.uWiggleFrequency;
            shader.uniforms.uWiggleIntensity =
                this.material.userData.uniforms.uWiggleIntensity;

            shader.vertexShader =
                `
                uniform sampler2D texturePosition;
                uniform sampler2D textureVelocity;
                uniform float uTimeB;
                uniform float uWiggleFrequency;
                uniform float uWiggleIntensity;
                attribute vec2 reference;
                attribute float wiggleOffset;
                attribute float wiggleSpeed;
            ` + shader.vertexShader;

            shader.vertexShader = shader.vertexShader.replace(
                "#include <beginnormal_vertex>",
                `
                vec3 boidVel = texture2D(textureVelocity, reference).xyz;

                // Rotation matrix from velocity (look-at)
                vec3 forward = normalize(boidVel);
                vec3 up = vec3(0.0, 1.0, 0.0);
                if (abs(forward.y) > 0.99) up = vec3(1.0, 0.0, 0.0);
                vec3 right = normalize(cross(up, forward));
                up = cross(forward, right);

                mat3 boidRotation = mat3(right, up, forward);

                vec3 objectNormal = boidRotation * normal;
                `,
            );

            shader.vertexShader = shader.vertexShader.replace(
                "#include <begin_vertex>",
                `
                vec3 boidPos = texture2D(texturePosition, reference).xyz;

                // Wiggle animation
                // Assuming Z is forward: Head at +Z, Tail at -Z (for 0.8 length: -0.4 to 0.4)
                float tailFactor = smoothstep(0.8, -0.4, position.z);
                float wiggleInterval = sin(uTimeB * 3. + wiggleOffset) * 0.5 + 0.5; // oscillates between 0 and 1
                float wiggle = sin(uTimeB * wiggleSpeed + position.z * uWiggleFrequency + wiggleOffset) * uWiggleIntensity * wiggleInterval * tailFactor;
                
                vec3 wiggledPosition = position;
                wiggledPosition.x += wiggle;

                vec3 transformed = boidRotation * wiggledPosition + boidPos;
                `,
            );
        };

        this.mesh = new THREE.InstancedMesh(
            geometry,
            this.material,
            this.count,
        );
        this.mesh.castShadow = true;
        // Commenting this out as fish casting shadows on other fish is too messy right now
        // this.mesh.receiveShadow = true;

        // Custom depth material for shadows
        this.mesh.customDepthMaterial = new THREE.MeshDepthMaterial({
            depthPacking: THREE.RGBADepthPacking,
        });

        this.mesh.customDepthMaterial.userData.uniforms =
            this.material.userData.uniforms;

        this.mesh.customDepthMaterial.onBeforeCompile = (shader) => {
            shader.uniforms.texturePosition =
                this.mesh.customDepthMaterial.userData.uniforms.texturePosition;
            shader.uniforms.textureVelocity =
                this.mesh.customDepthMaterial.userData.uniforms.textureVelocity;
            shader.uniforms.uTimeB =
                this.mesh.customDepthMaterial.userData.uniforms.uTimeB;
            shader.uniforms.uWiggleFrequency =
                this.mesh.customDepthMaterial.userData.uniforms.uWiggleFrequency;
            shader.uniforms.uWiggleIntensity =
                this.mesh.customDepthMaterial.userData.uniforms.uWiggleIntensity;

            shader.vertexShader =
                `
                uniform sampler2D texturePosition;
                uniform sampler2D textureVelocity;
                uniform float uTimeB;
                uniform float uWiggleFrequency;
                uniform float uWiggleIntensity;
                attribute vec2 reference;
                attribute float wiggleOffset;
                attribute float wiggleSpeed;
            ` + shader.vertexShader;

            shader.vertexShader = shader.vertexShader.replace(
                "#include <begin_vertex>",
                `
                vec3 boidPos = texture2D(texturePosition, reference).xyz;
                vec3 boidVel = texture2D(textureVelocity, reference).xyz;

                // Rotation matrix from velocity (look-at)
                vec3 forward = normalize(boidVel);
                vec3 up = vec3(0.0, 1.0, 0.0);
                if (abs(forward.y) > 0.99) up = vec3(1.0, 0.0, 0.0);
                vec3 right = normalize(cross(up, forward));
                up = cross(forward, right);

                mat3 boidRotation = mat3(right, up, forward);

                // Wiggle animation
                float tailFactor = smoothstep(0.8, -0.4, position.z);
                float wiggleInterval = sin(uTimeB * 3.0 + wiggleOffset) * 0.5 + 0.5; // oscillates between 0 and 1
                float wiggle = sin(uTimeB * wiggleSpeed + position.z * uWiggleFrequency + wiggleOffset) * uWiggleIntensity * wiggleInterval * tailFactor;
                
                vec3 wiggledPosition = position;
                wiggledPosition.x += wiggle;

                vec3 transformed = boidRotation * wiggledPosition + boidPos;
                `,
            );
        };
    }

    setDebug() {
        this.debugFolder = this.debug.ui.addFolder("Boids (GPGPU)");
        const velUniforms = this.velocityVariable.material.uniforms;

        this.debugFolder
            .add(PARAMS, "perceptionRadius", 1, 20, 0.1)
            .name("Perception Radius")
            .onChange((v) => (velUniforms.perceptionRadius.value = v));
        this.debugFolder
            .add(PARAMS, "separationRadius", 0.1, 10, 0.1)
            .name("Separation Radius")
            .onChange((v) => (velUniforms.separationRadius.value = v));
        this.debugFolder
            .add(PARAMS, "obstacleRadius", 1, 30, 0.1)
            .name("Obstacle Radius")
            .onChange((v) => (velUniforms.uObstacleRadius.value = v));
        this.debugFolder
            .add(PARAMS, "avoidanceSamples", 5, 64, 1)
            .name("Avoidance Samples")
            .onChange((v) => (velUniforms.uAvoidanceSamples.value = v));
        this.debugFolder
            .add(PARAMS, "avoidanceWeight", 0, 10, 0.1)
            .name("Avoidance Weight")
            .onChange((v) => (velUniforms.uAvoidanceWeight.value = v));
        this.debugFolder
            .add(PARAMS, "maxSpeed", 0.01, 1.0, 0.01)
            .name("Max Speed")
            .onChange((v) => (velUniforms.maxSpeed.value = v));
        this.debugFolder
            .add(PARAMS, "maxForce", 0.001, 0.1, 0.001)
            .name("Max Force")
            .onChange((v) => (velUniforms.maxForce.value = v));
        this.debugFolder
            .add(PARAMS, "separationWeight", 0, 5, 0.1)
            .name("Separation Weight")
            .onChange((v) => (velUniforms.separationWeight.value = v));
        this.debugFolder
            .add(PARAMS, "alignmentWeight", 0, 5, 0.1)
            .name("Alignment Weight")
            .onChange((v) => (velUniforms.alignmentWeight.value = v));
        this.debugFolder
            .add(PARAMS, "cohesionWeight", 0, 5, 0.1)
            .name("Cohesion Weight")
            .onChange((v) => (velUniforms.cohesionWeight.value = v));
        this.debugFolder
            .add(PARAMS, "originForceWeight", 0, 0.01, 0.0001)
            .name("Origin Pull")
            .onChange((v) => (velUniforms.originForceWeight.value = v));
        this.debugFolder
            .add(PARAMS, "boundaryForceWeight", 0, 0.1, 0.001)
            .name("Boundary Force")
            .onChange((v) => (velUniforms.boundaryForceWeight.value = v));

        this.debugFolder
            .add(PARAMS, "wanderWeight", 0, 1, 0.01)
            .name("Wander Weight")
            .onChange((v) => (velUniforms.wanderWeight.value = v));
        this.debugFolder
            .add(PARAMS, "wanderDistance", 1, 10, 0.1)
            .name("Wander Distance")
            .onChange((v) => (velUniforms.wanderDistance.value = v));
        this.debugFolder
            .add(PARAMS, "wanderRadius", 0.1, 5, 0.1)
            .name("Wander Radius")
            .onChange((v) => (velUniforms.wanderRadius.value = v));
        this.debugFolder
            .add(PARAMS, "wanderSpeed", 0.1, 5, 0.1)
            .name("Wander Speed")
            .onChange((v) => (velUniforms.wanderSpeed.value = v));

        this.debugFolder = this.debug.ui.addFolder("Boids Wiggle");
        const meshUniforms = this.material.userData.uniforms;

        this.debugFolder
            .add(PARAMS, "wiggleFrequency", 0, 20, 0.1)
            .name("Wiggle Frequency")
            .onChange((v) => (meshUniforms.uWiggleFrequency.value = v));
        this.debugFolder
            .add(PARAMS, "wiggleIntensity", 0, 1, 0.01)
            .name("Wiggle Intensity")
            .onChange((v) => (meshUniforms.uWiggleIntensity.value = v));
    }

    update(delta = 16) {
        // Update simulation uniforms
        // delta is time in ms. The simulation expects delta where 16ms is "1 unit" of time
        this.positionVariable.material.uniforms.delta.value = delta;
        this.velocityVariable.material.uniforms.delta.value = delta;
        this.velocityVariable.material.uniforms.uTimeB.value =
            this.experience.time.elapsed * 0.001;

        this.gpuCompute.compute();

        const posTex = this.gpuCompute.getCurrentRenderTarget(
            this.positionVariable,
        ).texture;
        const velTex = this.gpuCompute.getCurrentRenderTarget(
            this.velocityVariable,
        ).texture;

        this.material.userData.uniforms.texturePosition.value = posTex;
        this.material.userData.uniforms.textureVelocity.value = velTex;
        this.material.userData.uniforms.uTimeB.value =
            this.experience.time.elapsed * 0.001;
    }
}
